import React from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { adjustStock } from "../redux/actions/stockLevelActions";
import { cleanFormData } from "../helpers/formHelpers";

import { InventoryLogType } from "../shared/enums/InventoryLogType";
import { StockAdjustmentRequestDto } from "../shared/dtos/stockLevel/StockAdjustmentRequestDto";
import { StockLevelResponseDto } from "../shared/dtos/stockLevel/StockLevelResponseDto";
import { stockLevelActions } from "../redux/slices/stockLevel";
const LOG_TYPES = [
  {
    value: InventoryLogType.ADJUSTMENT_COUNT,
    label: "Inventory Count (Add/Deduct)",
  },
  {
    value: InventoryLogType.ADJUSTMENT_DAMAGE,
    label: "Damage / Loss (Deduct)",
  },
];

const adjustStockSchema = Yup.object().shape({
  logType: Yup.string()
    .oneOf(Object.values(InventoryLogType))
    .required("Adjustment type is required"),
  quantityChange: Yup.number()
    .required("Quantity change is required")
    .notOneOf([0], "Quantity change cannot be zero"),
  referenceNumber: Yup.string().nullable(),
  notes: Yup.string().nullable(),
});

interface AdjustStockFormProps {
  stockLevel: StockLevelResponseDto;
  loading: boolean;
  error: string | null;
}

const AdjustStockForm: React.FC<AdjustStockFormProps> = ({
  stockLevel,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: Partial<StockAdjustmentRequestDto> = {
    logType: InventoryLogType.ADJUSTMENT_COUNT,
    quantityChange: "" as unknown as number,
    referenceNumber: "",
    notes: "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(stockLevelActions.setErrorAdjust(null))}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={adjustStockSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(
            values,
          ) as Partial<StockAdjustmentRequestDto>;

          const payload: StockAdjustmentRequestDto = {
            ...cleanedValues,
            inventoryItemId: stockLevel.inventoryItemId,
            branchId: stockLevel.branchId,
            logType: cleanedValues.logType as InventoryLogType,
            quantityChange: Number(cleanedValues.quantityChange),
          };

          dispatch(adjustStock(payload));
        }}
      >
        {({
          dirty,
          isValid,
          values,
        }: FormikProps<Partial<StockAdjustmentRequestDto>>) => (
          <Form>
            <Stack spacing={2}>
              {/* Display Context Information */}
              <Box
                sx={{
                  bgcolor: "background.default",
                  p: 2,
                  borderRadius: 1,
                  border: "1px dashed #ccc",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Item:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: "bold",
                    mb: 1,
                  }}
                >
                  {stockLevel.itemName} ({stockLevel.itemCode})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Stock:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  {stockLevel.quantity} {stockLevel.unitOfMeasure}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr" },
                }}
              >
                <FormSelectField
                  name="logType"
                  label="Adjustment Type"
                  options={LOG_TYPES}
                />
                <FormTextField
                  name="quantityChange"
                  label="Quantity Change (+ or -)"
                  type="number"
                  placeholder="e.g., -5 for damages, 10 for found stock"
                  helperText={
                    values.logType === InventoryLogType.ADJUSTMENT_DAMAGE
                      ? "Use a negative number to deduct damaged stock."
                      : "Use positive to add, negative to deduct."
                  }
                />

                <FormTextField
                  name="referenceNumber"
                  label="Reference Number (Optional)"
                  placeholder="e.g., INC-2024-01"
                />
                <FormTextField
                  name="notes"
                  label="Notes / Reason"
                  multiline
                  rows={2}
                  placeholder="Explain the reason for this adjustment..."
                />
              </Box>

              <SubmitButton
                title="Apply Adjustment"
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={<SaveOutlinedIcon />}
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AdjustStockForm;
